import { z } from "zod/v4";
import {
  AgenticSettingsReviewOpType,
  PROPOSED_ENTITY_KEY_PATTERN,
} from "../../types";
import {
  ASSET_COLLECTION_MEDIA_KINDS,
  LIBRARY_UPLOAD_LEFT_OUT_REASONS,
} from "./constants";

// The asset library operations exactly as the agent writes them. One schema per op
// validates both the wire output and the persisted jsonb.
//
// SCHEMA ONLY. The frontend compiles this through the llm/schema barrel, so keep it free
// of database, storage, and other node-only imports.

const MAX_ASSET_COLLECTION_NAME_LENGTH = 100;

const newCollectionSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1)
    .max(MAX_ASSET_COLLECTION_NAME_LENGTH)
    .describe(
      "Display name for the new asset library collection. Must not match a collection in the index.",
    ),
  media_kind: z
    .enum(ASSET_COLLECTION_MEDIA_KINDS)
    .describe(
      "What the collection holds: image, video, component (SVG), or any of them.",
    ),
  brand_profile_ids: z
    .array(z.string().min(1))
    .describe(
      "Brand profile ids from list_brand_profiles that the collection is scoped to. [] applies it to every brand.",
    ),
});

const addAssetsOperationSchema = z.object({
  op_type: z.literal(AgenticSettingsReviewOpType.AddAssets),
  files: z
    .array(
      z.object({
        file_id: z.string().min(1),
        title: z.string().trim().min(1).max(200),
        description: z.string().trim().max(2000).optional(),
      }),
    )
    .min(1)
    .max(500),
  left_out: z
    .array(
      z.object({
        file_id: z.string().min(1),
        reason: z.enum(LIBRARY_UPLOAD_LEFT_OUT_REASONS),
        detail: z.string().trim().min(1).max(500),
      }),
    )
    .max(500)
    .default([]),
  collection_id: z
    .string()
    .min(1)
    .optional()
    .describe(
      "An existing writable collection's id from list_library_collections. Set this or new_collection_key, not both.",
    ),
  new_collection: newCollectionSchema
    .optional()
    .describe(
      "Required for a new collection; approval creates it and imports its files as one decision.",
    ),
  new_collection_key: z
    .string()
    .regex(PROPOSED_ENTITY_KEY_PATTERN)
    .optional()
    .describe(
      "Stable slug for this proposed collection; reuse it when revising the proposal.",
    ),
});

export const ASSET_WIRE_OPERATIONS = [addAssetsOperationSchema] as const;

export const ASSET_STORED_OPERATIONS = [
  addAssetsOperationSchema.extend({
    import_job_ids: z.array(z.uuid()).optional(),
  }),
] as const;
